import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import ButtonTextLoading from "../Shared/ButtonTextLoading";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import FormControl from "@material-ui/core/FormControl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getUserGroups,
  registerAdminUser,
  verifyUsernameExists,
} from "../../../service/endpoint-manager";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@mui/material/MenuItem";
import { useSnackbar } from "notistack";

import styles from "../../../styles.module.css"; // Importing CSS module for styles.

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },

  formControl: {
    width: "100%",
  },

  menuItems: { display: "block" },

  dropdown: {
    borderTopRightRadius: "0",
    borderBottomRightRadius: "0",

    marginRight: "5%",
    backgroundColor: "#f7f7f7",
    width: "100%",
  },
}));

export default function CreateUser({
  setOpenAddUserGroupDialog,
  users,
  setUsers,
}) {
  const classes = useStyles();
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userGroups, setUserGroups] = useState(null); //represents all user groups
  const [selectedUserGroup, setSelectedUserGroup] = useState(null); //represented the currently selected userGroup
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleUserGroupChange = (e) => {
    setSelectedUserGroup(e.target.value);
  };

  const formik = useFormik({
    initialValues: {
      fullName: "",
      username: "",
      password: "",
      confirmPassword: "",
      userGroup: "",
    },
    onSubmit: async function (values) {
      setIsSubmitting(true);
      setIsLoading(true);
      const result = await registerAdminUser(
        values.password,
        values.username,
        values.userGroup,
        values.fullName
      );

      if (result.status === 200) {
        setUsers([...users, result?.data?.body]);
        enqueueSnackbar(`${result?.data?.message}`, {
          variant: "success",
        });
        setOpenAddUserGroupDialog(false);
      } else {
        enqueueSnackbar(`${result?.response?.data?.message}`, {
          variant: "error",
        });
      }
      setIsLoading(false);
      setIsSubmitting(false);
    },

    // validation schema for formik using yup
    validationSchema: Yup.object({
      // name must be text only and is required
      fullName: Yup.string()
        .matches(/^[a-zA-Z]+(?:[\s.]+[a-zA-Z]+)*$/, "name must be text only")
        .label("Full Name")
        .required("Full name is required"),

      // username must be 6-20 and is required
      username: Yup.string()
        .min(6, "At least 6 characters required")
        .max(20)
        .required("Username is required"),

      // password must be at least 6 characters and is required
      password: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Password is required"),
      // password confirmation must be at least 6 characters and is required
      confirmPassword: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Password is required"),

      userGroup: Yup.string().required("userGroup is required"),
    }),
  });

  // this useEffect checks to make sure both password and confirmPassword values are supplied
  //and match - sets state accordingly
  useEffect(() => {
    if (
      formik.values.password.length > 0 &&
      formik.values.confirmPassword.length > 0 &&
      formik.values.password !== formik.values.confirmPassword
    ) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  }, [formik.values.password, formik.values.confirmPassword]);

  // this useEffect gets user group info when component initially renders
  useEffect(() => {
    const getUserGroupsInfo = async () => {
      setIsLoading(true);
      const result = await getUserGroups();
      if (result?.status === 200) {
        setUserGroups(result?.data?.body?.Items);
      }
      setIsLoading(false);
    };

    getUserGroupsInfo();
  }, []);

  // debounced useEffect to check if username exists
  useEffect(() => {
    if (formik.values.username.length > 0 && !formik.errors.username) {
      setCheckingUsername(true);
      setUsernameExists(false);
      const getData = async (username) => {
        let res = await verifyUsernameExists(username);
        if (res.data.status === true) {
          setUsernameExists(true);
        } else {
          setUsernameExists(false);
        }
        setCheckingUsername(false);
      };

      // Function launches after 1.5 seconds (1500 ms) of the last keystroke
      // On first render you don't want to launch anything
      // Thus, you check if the user typed a query at first
      let timer = setTimeout(() => {
        if (formik.values.username) getData(formik.values.username);
      }, 3000);

      // If useEffect() relaunches, you clear the function
      // That means, the previous function won't launch
      // Thus, won't send a request to the API
      return () => clearTimeout(timer);
    } else {
      setCheckingUsername(false);
      setUsernameExists(false);
    }
  }, [formik.values.username]);

  const showUsernameValidationMessage =
    (formik.touched.username && formik.errors.username) ||
    checkingUsername ||
    (!checkingUsername && usernameExists) ||
    (!checkingUsername && !usernameExists);

  // customized validation message for username depending on situation
  const determineValidationMessage = () => {
    if (formik.touched.username && formik.errors.username) {
      return formik.errors.username || "Username required";
    }

    if (checkingUsername) {
      return "Checking Availability...";
    }

    if (usernameExists) {
      return "Username is already taken!";
    }
    if (!usernameExists && formik.values.username.length > 0) {
      return "Username is available!";
    }
  };

  // customized color for username validation message depending on situation
  const getUsernameValidationMessageColor = () => {
    if ((formik.touched.username && formik.errors.username) || usernameExists) {
      return "red";
    }
    if (checkingUsername) {
      return "#3f51b5";
    }
    if (!usernameExists) {
      return "green";
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />

      {isLoading ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              height: "60vh",
              // width: "30vh",
            }}
          >
            <CircularProgress />
          </div>
        </>
      ) : (
        <>
          <div className={styles.paper}>
            <Typography component="h1" variant="h6" className={styles.heading}>
              Create user
            </Typography>
            <form
              onSubmit={formik.handleSubmit}
              className={classes.form}
              noValidate
            >
              <Grid container spacing={2}>
                {/* full name */}
                <Grid item xs={12}>
                  <TextField
                    error={formik.touched.fullName && formik.errors.fullName}
                    autoComplete="full-name"
                    name="fullName"
                    required
                    fullWidth
                    id="fullName"
                    label="Full Name"
                    variant="outlined"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.fullName}
                  />
                  {
                    <div
                      style={{
                        color: "red",
                        fontSize: "0.6rem",
                        visibility:
                          formik.touched.fullName && formik.errors.fullName
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      {formik.errors.fullName || "Full name is required"}
                    </div>
                  }
                </Grid>

                {/* username */}
                <Grid item xs={12}>
                  <TextField
                    error={formik.touched.username && formik.errors.username}
                    required
                    fullWidth
                    id="username"
                    label="Username"
                    name="username"
                    autoComplete="username"
                    variant="outlined"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.username}
                  />
                  {
                    <div
                      style={{
                        color: getUsernameValidationMessageColor(),
                        fontSize: "0.6rem",
                        visibility: showUsernameValidationMessage
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      {determineValidationMessage()}
                    </div>
                  }
                </Grid>

                {/* userGroup select */}
                <Grid item xs={12}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <InputLabel htmlFor="userGroup">User Group</InputLabel>
                    <Select
                      variant="outlined"
                      defaultValue={"none"}
                      value={formik.values.userGroup}
                      displayEmpty
                      onChange={formik.handleChange}
                      label="userGroup"
                      className={classes.dropdown}
                      inputProps={{
                        name: "userGroup",
                        id: "existing-userGroup-Selector",
                      }}
                    >
                      <MenuItem value="none" disabled>
                        Select a UserGroup
                      </MenuItem>
                      {userGroups?.map(
                        (userGroup, index) =>
                          userGroup?.template == false && (
                            <MenuItem
                              key={userGroup?.ID}
                              selected={userGroup?.ID}
                              value={userGroup?.ID}
                              className={classes.menuItems}
                            >
                              {userGroup?.name}
                            </MenuItem>
                          )
                      )}
                    </Select>
                  </FormControl>
                  {
                    <div
                      style={{
                        color: "red",
                        fontSize: "0.6rem",
                        visibility: formik.errors.userGroup
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      {formik.errors.userGroup || "user group required"}
                    </div>
                  }
                </Grid>

                {/* password */}
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                  />
                  {
                    <div
                      style={{
                        color: "red",
                        fontSize: "0.6rem",
                        visibility:
                          formik.touched.password && formik.errors.password
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      {formik.errors.password || "Password required"}
                    </div>
                  }
                </Grid>

                {/* password confirmation */}
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    id="confirmPassword"
                    autoComplete="confirm-password"
                    variant="outlined"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmPassword}
                  />
                  {
                    <div style={{ display: "flex" }}>
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.6rem",
                          visibility:
                            formik.touched.confirmPassword &&
                            formik.errors.confirmPassword
                              ? "visible"
                              : "hidden",
                        }}
                      >
                        {formik.errors.confirmPassword}
                      </div>
                      {/* will be shown if passwords dont match as an error message */}
                      {
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.6rem",
                            visibility: !passwordsMatch ? "visible" : "hidden",
                          }}
                        >
                          {formik.errors.confirmPassword
                            ? `, Passwords don't match`
                            : `Passwords don't match`}
                        </div>
                      }
                    </div>
                  }
                </Grid>
              </Grid>

              {/* submit button */}
              <Button
                type="submit"
                // button will be disabled if form has errors or passwords dont match
                disabled={
                  Object.keys(formik.errors).length > 0 ||
                  !passwordsMatch ||
                  checkingUsername ||
                  usernameExists
                }
                fullWidth
                variant="contained"
                color="primary"
                className={styles.primaryButton}
                style={{ marginBottom: "20px", marginTop: "20px" }}
              >
                <ButtonTextLoading
                  isSubmitting={isSubmitting}
                  body={"Create User"}
                />
              </Button>
            </form>
          </div>
        </>
      )}
    </Container>
  );
}
